import React from 'react';
import {Button} from 'reactstrap';
import SessionStateManager from "../../common/SessionStateManager";
import PropTypes from 'prop-types';
import _ from 'lodash';

class ReactWizard extends React.Component {

    constructor(props) {
        super(props);

        const session = new SessionStateManager(props.sessionKey || "react.wizard.session", !!props.sessionKey);
        this.session = session;
        this.state = {
            currentPhase: session.getState('currentPhase', 0),
            isCompleted: session.getState('isCompleted', false),
            showControls: props.showControls,
            invokedActivate: false,
            wizardState: session.getState('wizardState', props.initialValues || {}),
            previousPhases: []
        };

        this.moveNextPhase = this.moveNextPhase.bind(this);
        this.movePreviousPhase = this.movePreviousPhase.bind(this);

    }

    getActivePhase() {
        return this.state.currentPhase;
    }

    componentWillMount() {

        window.onbeforeunload = (evt) => {

            if (this.props.sessionKey) {
                this.persistSession();
            }

        };
    }

    componentDidMount() {

        const {currentPhase} = this.state;

        //
        if (this._children) {

            let activate = this._children[currentPhase].props.onActivate;
            if (activate)
                activate();
        }


    }

    persistSession() {

        this.session.putStateFrame({
            currentPhase: this.state.currentPhase,
            wizardState: this.state.wizardState
        }).save();
    }

    componentWillUnmount() {
        if (this.props.sessionKey) {
            this.persistSession();
        }
    }


    /**
     * Checks whether the wizard is complete
     * @returns {boolean}
     */
    isComplete() {
        return this.state.isCompleted;
    }

    /**
     * Advance phase
     */
    moveNextPhase() {

        const me = this;

        if (this.isComplete())
            return;

        //
        let nextPhase = this.state.currentPhase + 1;
        if (nextPhase >= this._children.length) {

            this.movePhase(Math.min(nextPhase, this._children.length - 1), (phase) => {

                me.setState({isComplete: true, currentPhase: phase});

                //  call complete
                if (me.props.onComplete)
                    me.props.onComplete();

            }, true);

        } else {
            this.movePhase(Math.min(nextPhase, this._children.length - 1));
        }

    }


    reset() {

        this.session.reset();

        this.setState({
            isBusy: false,
            isCompleted: false,
            currentPhase: 0,
            previousPhases: []
        });

    }

    canMoveNext() {
        return this.isComplete() ? false : this.state.currentPhase < this._children.length;
    }

    /**
     * Checks whether previous phase navigation is possible
     * @returns {boolean}
     */
    canMovePrev() {
        return this.isComplete() ? false : this.state.currentPhase > 0;
    }

    /**
     * Indicates whether back navigation is possible
     * @return {boolean}
     */
    canMoveBack() {
        return this.state.previousPhases.length > 0;
    }

    offsetPhase(offset, proceedHook = null, trackPhase = true) {
        return this.movePhase(this.state.currentPhase + offset, proceedHook, trackPhase);
    }

    movePhase(nextPhase, proceedHook = null, trackPhase = true) {

        const {currentPhase} = this.state;

        if (trackPhase) {
            this.setState(state => ({

                previousPhases: [
                    ...state.previousPhases,
                    currentPhase
                ]

            }));
        }


        if (proceedHook != null) {
            proceedHook(nextPhase);
        } else {

            //
            this.setState({
                currentPhase: nextPhase,
                invokedActivate: false
            });

            //
            let activate = this._children[nextPhase].props.onActivate;
            if (activate)
                activate();

        }
    }

    movePreviousPhase() {
        this.movePhase(Math.max(this.state.currentPhase - 1, 0), null, false);
    }

    moveBack = () => {

        const {currentPhase, previousPhases} = this.state;
        if (previousPhases.length > 0) {

            const lastPhase = previousPhases[previousPhases.length - 1];
            previousPhases.pop();

            this.setState({
                previousPhases
            }, () => {
                this.offsetPhase(-(currentPhase - lastPhase), null, false);
            });
        }
    };


    /**
     * Get wizard context
     * @returns {{next: ReactWizard.moveNextPhase, getValue: (function(*): *), resetValues: resetValues, getValues: (function(): *), prev: ReactWizard.movePreviousPhase, setValue: setValue, step: *, isCompleted: boolean}}
     */
    getWizardContext = () => {

        return {
            wizard: this,
            step: this.getActivePhase(),
            isCompleted: this.isComplete(),
            next: this.moveNextPhase,
            prev: this.movePreviousPhase,
            back: this.moveBack,
            getValues: () => _.clone(this.state.wizardState),
            getValue: (key, defaultValue) => this.state.wizardState.hasOwnProperty(key) ? this.state.wizardState[key] : defaultValue,
            resetValues: (values) => {

                this.setState({

                    wizardState: _.isNil(values) ? {} : {
                        ...values
                    }

                })

            },
            deleteValues: (key) => {

                const {wizardState} = this.state;
                if (Array.isArray(key)) {
                    key.forEach(k => delete wizardState[k]);
                } else if (_.isString(key))
                    delete wizardState[key];

                this.setState({
                    wizardState
                });


            },
            setValue: (key, value) => {

                this.setState(state => ({
                    wizardState: {
                        ...state.wizardState,
                        [key]: value
                    }
                }))
            }
        }
    };

    _getInnerChildren(context) {

        return this._children = (_.isFunction(this.props.children) ?
            React.Children.only(this.props.children(context)).props.children :
            React.Children.toArray(this.props.children)).filter(x => x && x.props && x.props.__typeName === "WizardPhase");
    }

    render() {

        const currentPhase = this.state.currentPhase;
        const wizardContext = this.getWizardContext();
        const _actualChildren = this._getInnerChildren(wizardContext);

        return (<div className={this.props.containerClasses}>
            {_actualChildren.map((View, index) => {

                return (<div key={index} style={currentPhase === index ? null : {'display': 'none'}}>
                    {React.cloneElement(View, {
                        context: wizardContext,
                        isActive: currentPhase === index,
                        renderAlways: this.props.renderAlways
                    })}
                </div>);
            })}
            {this.state.showControls && <div>
                <Button disabled={this.state.currentPhase <= 0}
                        onClick={() => this.movePreviousPhase()}>Previous</Button>
                <Button disabled={this.state.currentPhase >= _actualChildren.length - 1}
                        onClick={() => this.moveNextPhase()}>Next</Button>
            </div>}

        </div>)
    }

}

const WizardPhase = ({children, context, renderAlways, isActive, containerClasses, style, component}) => {
    const WizardComponent = component;
    return <div className={containerClasses} style={style}>
        {renderAlways ? (WizardComponent ?
                <WizardComponent context={context}/> : (_.isFunction(children) ? children(context) : children)) :
            isActive && (WizardComponent ?
                <WizardComponent context={context}/> : (_.isFunction(children) ? children(context) : children))}
    </div>
};


WizardPhase.propTypes = {
    name: PropTypes.string,
    onChange: PropTypes.func,
    onActivate: PropTypes.func,
    containerClasses: PropTypes.string,
    style: PropTypes.object,
    component: PropTypes.elementType
};

WizardPhase.defaultProps = {
    __typeName: "WizardPhase"
};

ReactWizard.propTypes = {
    sessionKey: PropTypes.string,
    showControls: PropTypes.bool,
    onComplete: PropTypes.func,
    containerClasses: PropTypes.string,
    children: PropTypes.any,
    initialValues: PropTypes.object,
    renderAlways: PropTypes.bool
};

ReactWizard.defaultProps = {
    showControls: false,
    renderAlways: true
};

export {
    ReactWizard,
    WizardPhase
};



// WEBPACK FOOTER //
// ./src/components/ReactWizard/ReactWizard.js